import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useLocation, useNavigate } from "react-router-dom";
import ApCustomDivider from "./ApCustomDivider";
import { ThemeContext } from "../store/ThemeContextProvider";
import { errorToast } from "../utils/toastUtils";
import { ERRORS } from "../constants/errors";
import { getMenuManager } from "../managers/menuManager";
import PeopleIcon from "@mui/icons-material/People";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { UserContext } from "../store/UserContextProvider";
import { configureSidebarOptions } from "../utils/helpers";

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      marginTop: "64px",
      backgroundColor: theme.palette.background.default,
      height: "100%",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      marginTop: "64px",
      backgroundColor: theme.palette.background.default,
      height: "100%",
    },
  }),
}));

export default function Sidebar({ children }: { children: any }) {
  const themeContext = useContext(ThemeContext);
  const { isDrawerOpen: open, toggleDrawer, theme } = themeContext;
  const userContext = useContext(UserContext);
  const { userRoutes, setUserRoutes } = userContext;
  const [activeItem, setActiveItem] = useState<string>("/dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  const defaultRoute = useMemo(
    () => ({
      url: "/dashboard",
      name: "Dashboard",
      sequenceNumber: 0,
      icon: <DashboardIcon />,
    }),
    []
  );

  const fetchMenuOptions = useCallback(async () => {
    try {
      const { menuOptionsResponse, routeConfig } = await getMenuManager();
      const configuredOptions = configureSidebarOptions(
        menuOptionsResponse,
        routeConfig
      );
      const newMenuList = [defaultRoute, ...configuredOptions];
      setUserRoutes(newMenuList);
    } catch (error: any) {
      errorToast(error.message || ERRORS.BAD_REQUEST);
    }
  }, [defaultRoute, setUserRoutes]);

  useEffect(() => {
    if (userRoutes.length <= 1) {
      fetchMenuOptions();
    }
    if (location?.pathname) {
      setActiveItem(location?.pathname);
    }
  }, [fetchMenuOptions, userRoutes]);

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <CssBaseline />
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          {open && (
            <Typography
              sx={{
                textAlign: "center",
                color: theme.aapnoParichayTheme.primaryContainer,
                minWidth: "80%",
              }}
            >
              Menu
            </Typography>
          )}
          <IconButton onClick={toggleDrawer}>
            {!open ? (
              <ChevronRightIcon
                sx={{ color: theme.aapnoParichayTheme.primaryContainer }}
              />
            ) : (
              <ChevronLeftIcon
                sx={{ color: theme.aapnoParichayTheme.primaryContainer }}
              />
            )}
          </IconButton>
        </DrawerHeader>
        <ApCustomDivider />
        <List
          sx={{
            height: "calc(100vh - 167px)",
            overflowY: "auto",
            scrollbarWidth: "none" /* Firefox */,
            "-ms-overflow-style": "none" /* IE 10+*/,
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
          }}
        >
          {userRoutes.map(({ name, icon = <PeopleIcon />, url }, index) => (
            <ListItem
              key={`name-${index}`}
              disablePadding
              dense
              sx={{
                display: "block",
                backgroundColor:
                  activeItem === url
                    ? theme.aapnoParichayTheme.primaryContainer
                    : "none",
              }}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
                onClick={() => {
                  setActiveItem(url);
                  navigate(url);
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={name}
                  sx={{
                    opacity: open ? 1 : 0,
                    color:
                      activeItem === url
                        ? theme.aapnoParichayTheme.onPrimaryContainer
                        : theme.aapnoParichayTheme.primaryContainer,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <ApCustomDivider />
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
}
